import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import "./BarChart.css";
Chart.register(...registerables);

const EMPTY_ARRAY = [];
const SELECTED_BAR_COLOR = "rgba(35, 203, 216, 0.96)";
const COMPARED_BAR_COLOR = "rgba(231, 195, 90, 0.88)";
const MISSING_SELECTED_COLOR = "rgba(35, 203, 216, 0.2)";
const MISSING_COMPARED_COLOR = "rgba(231, 195, 90, 0.22)";
const MISSING_BORDER_COLOR = "rgba(181, 185, 192, 0.34)";

const alternatingBackgroundPlugin = {
  id: "alternatingBackgroundPlugin",
  beforeDraw: (chart) => {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;
    const chartArea = chart.chartArea;

    if (!xAxis?.ticks?.length || !chartArea) {
      return;
    }

    const colors = ["rgba(24, 36, 59, 0.78)", "rgba(35, 47, 67, 0.62)"];
    const sectionWidth = (chartArea.right - chartArea.left) / xAxis.ticks.length;

    ctx.save();
    for (let i = 0; i < xAxis.ticks.length; i++) {
      ctx.fillStyle = colors[i % colors.length];
      const x = chartArea.left + i * sectionWidth;
      const width =
        i === xAxis.ticks.length - 1 ? chartArea.right - x : sectionWidth;
      ctx.fillRect(x, chartArea.top, width, chartArea.bottom - chartArea.top);
    }
    ctx.restore();
  },
};

const WaterLevelChart = ({
  barData,
  selected,
  refinedLabel,
  zoomLevel = 1,
  onZoomChange,
  showZoomControls = false,
  series,
}) => {
  const scrollRef = useRef(null);
  const pinchDistanceRef = useRef(null);
  const pinchZoomLockRef = useRef(false);
  const [isCompact, setIsCompact] = useState(false);
  const visibleLabel = refinedLabel || EMPTY_ARRAY;
  const visibleData = barData?.data || [];
  const safeZoomLevel = Math.max(0, Math.min(2, Number(zoomLevel) || 0));
  const isDayView = selected === "Day";
  const showEveryXAxisLabel = !isDayView && safeZoomLevel === 0;
  const tickFontSize = isDayView && safeZoomLevel === 0 ? 7 : [8, 9, 11][safeZoomLevel];
  const pointWidth = [7, 18, 42][safeZoomLevel];
  const chartHeight = [300, 330, 370][safeZoomLevel];

  const transposeData = (data) => {
    const transposed = [];
    data.forEach((arr) => {
      arr.forEach((value, index) => {
        if (!transposed[index]) {
          transposed[index] = [];
        }
        transposed[index].push(value);
      });
    });
    return transposed;
  };
  const transposedData = transposeData(visibleData);
  const chartSeries =
    Array.isArray(series) && series.length > 0
      ? series
      : transposedData.map((values, i) => ({
          label: `Series ${i + 1}`,
          data: values,
        }));
  const normalizeChartValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const labelCount = Math.max(
    visibleLabel.length,
    ...chartSeries.map((item) => item.data?.length || 0)
  );
  const hasChartData = chartSeries.some((item) =>
    (item.data || []).some((value) => normalizeChartValue(value) !== null)
  );
  const chartMinWidth =
    safeZoomLevel === 0 && isCompact && isDayView
      ? 0
      : safeZoomLevel === 0 && isCompact
      ? Math.max(620, labelCount * 76)
      : safeZoomLevel === 0
      ? Math.max(620, showEveryXAxisLabel ? labelCount * 76 : 620)
      : Math.max(
          760,
          labelCount * pointWidth * (chartSeries.length > 1 ? 1.16 : 1)
        );
  const chartMinWidthStyle = chartMinWidth > 0 ? `${chartMinWidth}px` : "100%";
  const palette = [
    SELECTED_BAR_COLOR,
    COMPARED_BAR_COLOR,
    "rgba(148, 163, 184, 0.8)",
  ];
  const chartUnit =
    chartSeries.find((item) => item.unit)?.unit || barData?.unit || "L/min";
  const chartData = {
    labels: visibleLabel,
    datasets: chartSeries.map((item, i) => {
      const rawValues = (item.data || []).map(normalizeChartValue);
      const values = rawValues.map((value) => (value === null ? null : value));
      const numericValues = rawValues.filter((value) => Number.isFinite(value));
      const maxValue = numericValues.length ? Math.max(...numericValues) : 0;
      const minValue = numericValues.length ? Math.min(...numericValues) : 0;
      const opacityRange = 0.7; // Set the opacity range from 0.6 to 1
      const missingColor = i === 0 ? MISSING_SELECTED_COLOR : MISSING_COMPARED_COLOR;
      return {
        label: item.label,
        rawValues,
        data: values,
        backgroundColor: rawValues.map((value) => {
          if (value === null) {
            return missingColor;
          }
          if (chartSeries.length > 1) {
            return item.color || palette[i % palette.length];
          }
          if (maxValue === minValue) {
            // Handle case when all values are the same
            return SELECTED_BAR_COLOR;
          } else {
            const opacity =
              opacityRange +
              (1 - opacityRange) * ((value - minValue) / (maxValue - minValue));
            return `rgba(35, 203, 216, ${opacity})`;
          }
        }),
        borderColor: rawValues.map((value) =>
          value === null ? MISSING_BORDER_COLOR : "transparent"
        ),
        borderWidth: rawValues.map((value) => (value === null ? 1 : 0)),
        borderRadius: 3,
        barPercentage:
          chartSeries.length > 1
            ? [0.52, 0.62, 0.68][safeZoomLevel]
            : [0.88, 0.82, 0.78][safeZoomLevel],
        categoryPercentage:
          chartSeries.length > 1
            ? [0.62, 0.68, 0.72][safeZoomLevel]
            : [0.9, 0.86, 0.82][safeZoomLevel],
        barThickness: "flex", // Set bar thickness as flexible
        categorySpacing: 4, // Set the spacing between bars
        minBarLength: chartSeries.length > 1 ? 3 : undefined,
      };
    }),
  };

  const chartOptions = {
    indexAxis: "x",
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          font: {
            weight: "bold", // Set font weight to bold
            size: tickFontSize,
          },

          color: "#B5B9C0",
          align: "inner",
          labelOffset: 6,
          maxRotation: isDayView && safeZoomLevel === 0 ? 20 : 45,
          minRotation: isDayView && safeZoomLevel === 0 ? 0 : 35,
          autoSkip: isDayView && safeZoomLevel === 0,
          maxTicksLimit:
            safeZoomLevel === 0 ? (isDayView ? 24 : labelCount) : undefined,
        },
        grid: {
          color: "transparent",
        },
        border: {
          width: 2,
          color: "#0B1830",
        },
      },

      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: chartUnit,
          color: "#B5B9C0",
          font: {
            size: tickFontSize,
            weight: "bold",
          },
        },
        ticks: {
          font: {
            // weight: "bold", // Set font weight to bold
            size: tickFontSize,
          },
          color: "#B5B9C0",
          align: "inner",
          weight: "bold",
        },

        grid: {
          color: "#545D6E",
          lineWidth: 0.25,
        },

        border: {
          width: 2,
          color: "#0B1830",
        },
      },
    },
    plugins: {
      legend: {
        display: chartSeries.length > 1 && !isCompact,
        align: "end",
        labels: {
          boxHeight: 8,
          boxWidth: 8,
          color: "#B5B9C0",
          font: {
            size: 10,
          },
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const label = context.dataset?.label
              ? `${context.dataset.label}: `
              : "";
            const rawValue = context.dataset?.rawValues?.[context.dataIndex];
            if (rawValue === null) {
              return `${label}No data`;
            }
            const value = rawValue ?? context.parsed?.y;
            return Number.isFinite(value)
              ? `${label}${value.toFixed(2)} ${chartUnit}`
              : `${label}No data`;
          },
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  const handleWheel = (event) => {
    if (!onZoomChange || (!event.ctrlKey && !event.metaKey)) {
      return;
    }

    event.preventDefault();
    const direction = event.deltaY > 0 ? -1 : 1;
    onZoomChange((currentZoom) => {
      const current = Math.max(0, Math.min(2, Number(currentZoom) || 0));
      return Math.max(0, Math.min(2, current + direction));
    });
  };

  const getTouchDistance = (touches) => {
    if (!touches || touches.length < 2) {
      return null;
    }

    const first = touches[0];
    const second = touches[1];
    return Math.hypot(
      second.clientX - first.clientX,
      second.clientY - first.clientY
    );
  };

  const handleTouchStart = (event) => {
    if (!onZoomChange || event.touches.length !== 2) {
      pinchDistanceRef.current = null;
      pinchZoomLockRef.current = false;
      return;
    }

    pinchDistanceRef.current = getTouchDistance(event.touches);
    pinchZoomLockRef.current = false;
  };

  const handleTouchMove = (event) => {
    if (!onZoomChange || event.touches.length !== 2) {
      return;
    }

    const currentDistance = getTouchDistance(event.touches);
    const previousDistance = pinchDistanceRef.current;
    if (!currentDistance || !previousDistance) {
      pinchDistanceRef.current = currentDistance;
      return;
    }

    const delta = currentDistance - previousDistance;
    if (Math.abs(delta) < 18 || pinchZoomLockRef.current) {
      return;
    }

    event.preventDefault();
    pinchZoomLockRef.current = true;
    onZoomChange((currentZoom) => {
      const current = Math.max(0, Math.min(2, Number(currentZoom) || 0));
      return Math.max(0, Math.min(2, current + (delta > 0 ? 1 : -1)));
    });
    pinchDistanceRef.current = currentDistance;

    window.setTimeout(() => {
      pinchZoomLockRef.current = false;
    }, 140);
  };

  const handleTouchEnd = (event) => {
    if (event.touches.length < 2) {
      pinchDistanceRef.current = null;
      pinchZoomLockRef.current = false;
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 866px)");
    const syncCompactState = () => setIsCompact(mediaQuery.matches);

    syncCompactState();
    mediaQuery.addEventListener?.("change", syncCompactState);
    return () => mediaQuery.removeEventListener?.("change", syncCompactState);
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !hasChartData) {
      return;
    }

    window.requestAnimationFrame(() => {
      scrollElement.scrollLeft =
        isCompact || safeZoomLevel === 0 || labelCount <= 32
          ? 0
          : scrollElement.scrollWidth;
    });
  }, [hasChartData, chartMinWidth, isCompact, labelCount, safeZoomLevel, visibleLabel]);

  return (
    <div className="m-auto text-center">
      {hasChartData ? (
        <>
          {chartSeries.length > 1 && (
            <div className="flow-chart-native-legend" aria-label="Chart legend">
              {chartSeries.map((item, index) => (
                <span className="flow-chart-legend-item" key={item.label || index}>
                  <span
                    className="flow-chart-legend-dot"
                    style={{
                      background: item.color || palette[index % palette.length],
                    }}
                  />
                  {item.shortLabel || item.label || `Series ${index + 1}`}
                </span>
              ))}
            </div>
          )}
          {showZoomControls && onZoomChange && !isCompact && (
            <div className="flow-chart-topbar">
              <div className="flow-chart-zoom-controls">
                <button
                  type="button"
                  className="flow-chart-zoom-btn"
                  disabled={safeZoomLevel === 0}
                  onClick={() =>
                    onZoomChange((currentZoom) =>
                      Math.max(0, Math.min(2, (Number(currentZoom) || 0) - 1))
                    )
                  }
                  aria-label="Zoom out"
                  title="Zoom out"
                >
                  <span aria-hidden="true">-</span>
                </button>
                <button
                  type="button"
                  className="flow-chart-zoom-btn"
                  disabled={safeZoomLevel === 2}
                  onClick={() =>
                    onZoomChange((currentZoom) =>
                      Math.max(0, Math.min(2, (Number(currentZoom) || 0) + 1))
                    )
                  }
                  aria-label="Zoom in"
                  title="Zoom in"
                >
                  <span aria-hidden="true">+</span>
                </button>
              </div>
            </div>
          )}
          <div
            className="flow-chart-scroll"
            role="region"
            aria-label="Water flow chart"
            ref={scrollRef}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            <div
              className="flow-chart-canvas"
              style={{ minWidth: chartMinWidthStyle, height: `${chartHeight}px` }}
            >
              <Bar
                data={chartData}
                options={chartOptions}
                plugins={[alternatingBackgroundPlugin]}
              />
            </div>
          </div>
        </>
      ) : (
        <p className="d-flex items-center justify-center m-auto h-full text-center my-6">
          No Data Available for Selected Range.
        </p>
      )}
      <p className="months-bar mt-3">
        {selected || "Month"}
      </p>
    </div>
  );
};

export default WaterLevelChart;
